import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteAccount, getAccount } from '@/api/accounts';
import { getAudit } from '@/api/audit';
import { ApiError } from '@/api/client';
import { useAuth } from '@/auth/useAuth';
import { AlertDialog } from '@/components/ui/AlertDialog';
import { formatDateTime } from '@/lib/dateFormat';

type Tab = 'profile' | 'signin' | 'audit';

export function AccountDetail() {
  const { id } = useParams();
  const accountId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canMutate, isAdmin } = useAuth();
  const [tab, setTab] = useState<Tab>('profile');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const query = useQuery({ queryKey: ['account', accountId], queryFn: ({ signal }) => getAccount(accountId, signal) });
  const del = useMutation({
    mutationFn: () => deleteAccount(accountId),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['accounts'] }); navigate('/accounts'); },
  });

  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError || !query.data) return <p className="text-sm text-red">Could not load this account.</p>;

  const a = query.data;
  const needsTriage = a.Source === 'EntraCloud' && (a.OwnerPersonId === null || !a.Purpose);

  return (
    <section>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounts" className="text-xs text-navy hover:underline">← Accounts</Link>
          <h1 className="font-heading text-2xl font-bold text-navy">{a.DisplayName}</h1>
          <p className="text-sm text-darkGrey/80">{a.Subtype} · {a.Source}</p>
        </div>
        {canMutate && (
          <div className="flex gap-2">
            <Link to={`/accounts/${accountId}/edit`} className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-navy hover:bg-bg">Edit</Link>
            {isAdmin && (
              <button type="button" onClick={() => { del.reset(); setConfirmOpen(true); }} className="rounded border border-red/40 bg-white px-3 py-1.5 text-sm font-medium text-red hover:bg-red/5">Delete</button>
            )}
          </div>
        )}
      </div>

      {needsTriage && (
        <div role="alert" className="mb-4 rounded border border-orange/40 bg-orange/10 px-3 py-2 text-sm text-orange">
          This synced cloud account needs triage — set an owner and purpose before the next access review.
        </div>
      )}

      <div className="mb-4 flex gap-1 border-b border-border text-sm">
        {([['profile', 'Profile'], ['signin', 'Last sign-in'], ['audit', 'Audit']] as [Tab, string][]).map(([key, label]) => (
          <button key={key} type="button" onClick={() => setTab(key)}
            className={tab === key ? 'border-b-2 border-orange px-3 py-2 font-semibold text-navy' : 'px-3 py-2 text-darkGrey/70 hover:text-navy'}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <dl className="grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="UPN" value={a.UPN} />
          <Row label="Entra object ID" value={a.EntraObjectId} />
          <Row label="Owner" value={a.OwnerPersonName ?? (a.OwnerPersonId ? `#${a.OwnerPersonId}` : null)} />
          <Row label="Purpose" value={a.Purpose} />
          <Row label="Active" value={a.Active ? 'Yes' : 'No'} />
          <Row label="Notes" value={a.Notes} />
        </dl>
      )}

      {tab === 'signin' && (
        <dl className="grid grid-cols-1 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Last sign-in" value={a.LastSignIn ? formatDateTime(a.LastSignIn) : null} />
          <Row label="Last sync" value={a.LastSyncDate ? formatDateTime(a.LastSyncDate) : null} />
        </dl>
      )}

      {tab === 'audit' && <AuditTab accountId={accountId} />}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this account?"
        description={`${a.DisplayName} will be soft-deleted and hidden from the register.`}
        confirmLabel="Delete"
        destructive
        closeOnConfirm={false}
        onConfirm={() => del.mutate()}
      >
        {del.isError && (
          <p role="alert" className="text-xs text-red">
            {del.error instanceof ApiError ? del.error.message : 'Could not delete this account.'}
          </p>
        )}
      </AlertDialog>
    </section>
  );
}

function Row({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex gap-2">
      <dt className="w-40 shrink-0 font-semibold text-darkGrey">{label}</dt>
      <dd className="text-darkGrey/80">{value ? value : '—'}</dd>
    </div>
  );
}

function AuditTab({ accountId }: { accountId: number }) {
  const query = useQuery({
    queryKey: ['audit', 'SystemAccount', accountId],
    queryFn: ({ signal }) => getAudit({ entityType: 'SystemAccount', entityId: accountId }, signal),
  });
  if (query.isLoading) return <p className="text-sm text-darkGrey/70">Loading…</p>;
  if (query.isError) return <p className="text-sm text-red">Could not load audit history.</p>;
  if (!query.data || query.data.length === 0) return <p className="text-sm text-darkGrey/70">No audit history.</p>;
  return (
    <ul className="space-y-2 text-sm">
      {query.data.map((e) => (
        <li key={e.AuditId} className="rounded border border-border bg-white px-3 py-2">
          <span className="font-semibold text-navy">{e.Action}</span>
          <span className="text-darkGrey/70"> · {formatDateTime(e.ChangedDate)} · {e.ChangedBy}</span>
        </li>
      ))}
    </ul>
  );
}
